import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import SwitchButton from "shared/SwitchButton/SwitchButton";
import {
  getCurrentUser,
  updateNotificationPreferences,
} from "../../redux/reducers/UserSlice/UserSlice";
import { AppDispatch, RootState } from "../../redux/store";
import successHandler from "../../utils/helpers/SuccessHandler";

const Notifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useSelector((state: RootState) => state.user);

  const [sms, setSms] = useState(false);
  const [whatsapp, setWhatsapp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Sync from server
  useEffect(() => {
    if (currentUser) {
      setSms(currentUser.sms_transactional ?? false);
      setWhatsapp(currentUser.whatsapp_transactional ?? false);
    }
  }, [currentUser]);

  const handleSubmit = () => {
    setIsLoading(true);
    dispatch(
      updateNotificationPreferences({
        // When user enables a channel, both transactional and marketing turn on together
        sms_transactional:      sms,
        sms_marketing:          sms,
        whatsapp_transactional: whatsapp,
        whatsapp_marketing:     whatsapp,
        // Email is always on
        email_transactional:    true,
        email_marketing:        true,
      })
    )
      .unwrap()
      .then(() => {
        successHandler("Notification preferences updated successfully.");
        dispatch(getCurrentUser());
      })
      .catch((error) => {
        console.error("Error updating preferences:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isSaveDisabled =
    !currentUser?.email_verified ||
    (currentUser?.is_host && !currentUser?.host_verified);

  return (
    <div className="space-y-5">
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        Choose how you'd like to hear from us about bookings and updates.
      </p>

      {/* Email — always on, locked */}
      <div className="flex items-center justify-between py-3 border-b border-neutral-100 dark:border-neutral-700">
        <div>
          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            Email
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">
            Always on — required for booking confirmations
          </p>
        </div>
        <div className="relative inline-flex items-center h-6 w-11 bg-primary-500 rounded-full cursor-not-allowed opacity-60">
          <span className="inline-block h-4 w-4 bg-white rounded-full shadow transform translate-x-6" />
        </div>
      </div>

      {/* SMS */}
      <div className="border-b border-neutral-100 dark:border-neutral-700 pb-3">
        <SwitchButton
          name="sms"
          label="SMS"
          subLabel="Booking updates and offers sent to your phone."
          isChecked={sms}
          onChange={() => setSms((prev) => !prev)}
        />
      </div>

      {/* WhatsApp */}
      <div className="pb-3">
        <SwitchButton
          name="whatsapp"
          label="WhatsApp"
          subLabel="Booking updates and offers via WhatsApp."
          isChecked={whatsapp}
          onChange={() => setWhatsapp((prev) => !prev)}
        />
      </div>

      {/* Fine print */}
      <p className="text-xs text-neutral-400 dark:text-neutral-500">
        By enabling SMS or WhatsApp you consent to Nomadroof contacting you on
        your verified phone number for booking updates and promotions. You can
        opt out at any time.
      </p>

      {/* Save */}
      <div className="flex justify-end pt-2">
        <ButtonPrimary
          type="button"
          onClick={handleSubmit}
          disabled={isSaveDisabled}
          loading={isLoading}
        >
          Save preferences
        </ButtonPrimary>
      </div>
    </div>
  );
};

export default Notifications;